/* Small helpers to build merged lattice / bar geometries once */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

const _dir = new THREE.Vector3();
const _mid = new THREE.Vector3();
const _q = new THREE.Quaternion();
const UP = new THREE.Vector3(0, 1, 0);

/** Box bar from point a to point b with a square cross-section */
export function barBetween(a, b, thickness = 0.1, thicknessZ) {
  const av = Array.isArray(a) ? new THREE.Vector3(...a) : a;
  const bv = Array.isArray(b) ? new THREE.Vector3(...b) : b;
  _dir.subVectors(bv, av);
  const len = _dir.length();
  if (len < 1e-5) return null;
  const g = new THREE.BoxGeometry(thickness, len, thicknessZ != null ? thicknessZ : thickness);
  _q.setFromUnitVectors(UP, _dir.clone().normalize());
  g.applyQuaternion(_q);
  _mid.addVectors(av, bv).multiplyScalar(0.5);
  g.translate(_mid.x, _mid.y, _mid.z);
  return g;
}

/** Merge a list of geometries (nulls are skipped) into one BufferGeometry */
export function mergeAll(geos) {
  const list = geos.filter(Boolean);
  if (!list.length) return new THREE.BufferGeometry();
  const merged = mergeGeometries(list, false);
  list.forEach((g) => g.dispose());
  return merged;
}

/**
 * Square lattice truss along +X of given length.
 * size: side of the square section, segs: number of bays.
 */
export function latticeTruss(length, size = 0.6, segs = 12, bar = 0.05) {
  const h = size / 2;
  const geos = [];
  const x0 = -length / 2;
  const corners = [
    [h, h],
    [-h, h],
    [-h, -h],
    [h, -h],
  ];
  // longerons
  corners.forEach(([y, z]) => geos.push(barBetween([x0, y, z], [x0 + length, y, z], bar)));
  const step = length / segs;
  for (let i = 0; i <= segs; i++) {
    const x = x0 + i * step;
    // frame ring
    for (let c = 0; c < 4; c++) {
      const [y1, z1] = corners[c];
      const [y2, z2] = corners[(c + 1) % 4];
      geos.push(barBetween([x, y1, z1], [x, y2, z2], bar * 0.8));
    }
    // diagonals on each face
    if (i < segs) {
      const xn = x + step;
      for (let c = 0; c < 4; c++) {
        const [y1, z1] = corners[c];
        const [y2, z2] = corners[(c + 1) % 4];
        const flip = (i + c) % 2 === 0;
        geos.push(flip ? barBetween([x, y1, z1], [xn, y2, z2], bar * 0.6) : barBetween([x, y2, z2], [xn, y1, z1], bar * 0.6));
      }
    }
  }
  return mergeAll(geos);
}
